import React from "react";
import { Star, CheckCircle2 } from "lucide-react";

const brokers = [
  {
    id: "octa",
    name: "Octa",
    country: "Global",
    rating: 4.9,
    verified: true,
    hero: "/assets/Brokers/octa.jpg",
    regulation: "CySEC, SVG",
    minDeposit: "$25",
    platforms: "MT4, MT5",
    description:
      "Globally recognized platform with simple deposits, flexible spreads, and strong reliability.",
  },

  {
    id: "ibullcapital",
    name: "iBull Capital",
    country: "Mauritius",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/ibullcapital.jpg",
    regulation: "FSC Mauritius",
    minDeposit: "$100",
    platforms: "MT5, MT5 Mobile",
    description:
      "iBull Capital delivers cutting-edge trading technology, deep liquidity, and secure infrastructure under FSC Mauritius regulation.",
  },
  {
    id: "tattvam",
    name: "Tattvam",
    country: "UAE / Mauritius",
    rating: 4.7,
    verified: true,
    hero: "/assets/Brokers/tattvam.jpg",
    regulation: "SCA (UAE), FSC (Mauritius)",
    minDeposit: "$1,000+",
    platforms: "MT5, Acuity",
    description:
      "High-end brokerage with integrated AI analytics and strong Middle East presence.",
  },
  {
    id: "exness",
    name: "Exness",
    country: "Global",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/exness.jpg",
    regulation: "FCA, CySEC",
    minDeposit: "$10",
    platforms: "MT4, MT5",
    description:
      "Trusted global broker with ultra-fast execution and transparent pricing for all traders.",
  },
  {
    id: "fxpro",
    name: "FxPro",
    country: "U.K.",
    rating: 4.9,
    verified: true,
    hero: "/assets/Brokers/fxpro.jpg",
    regulation: "FCA, CySEC",
    minDeposit: "$100",
    platforms: "MT4, MT5, cTrader",
    description:
      "Award-winning broker offering powerful tools and tight spreads for active traders.",
  },
  {
    id: "zerodha",
    name: "Zerodha",
    country: "India",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/zerodha.jpg",
    regulation: "SEBI",
    minDeposit: "₹0",
    platforms: "Kite",
    description:
      "India’s largest retail broker offering zero brokerage on equity investments and lightning-fast Kite trading platform.",
  },

  {
    id: "icmarkets",
    name: "IC Markets",
    country: "Australia",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/icmarkets.jpg",
    regulation: "ASIC, CySEC",
    minDeposit: "$200",
    platforms: "MT4, MT5, cTrader",
    description:
      "Known for low spreads and deep liquidity, serving global traders with institutional conditions.",
  },

  {
    id: "pepperstone",
    name: "Pepperstone",
    country: "U.K.",
    rating: 4.7,
    verified: true,
    hero: "/assets/Brokers/pepperstone.jpg",
    regulation: "FCA, ASIC",
    minDeposit: "$200",
    platforms: "MT4, MT5, cTrader",
    description:
      "Fast, regulated, and trader-focused broker offering competitive pricing and analytics tools.",
  },
];

export default function BrokersList() {
  return (
    <div className="min-h-screen bg-slate-950/70 p-6 text-white">
      {/* Header */}
      <div className="mb-10 text-center">
        <h1 className="bg-gradient-to-r from-green-400 via-blue-400 to-purple-600 bg-clip-text text-4xl font-extrabold text-transparent sm:text-5xl">
          Verified Global Brokers
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          Explore trusted, regulated brokers connected through the
          Million2Trillion network.
        </p>
      </div>

      {/* 2 Cards per Row */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {brokers.map((b) => (
          <div
            key={b.id}
            className="flex flex-col items-center gap-4 rounded-2xl border border-slate-800 bg-gradient-to-br from-[#101422] to-[#0B0F19] p-5 shadow-[0_0_25px_-10px_rgba(56,189,248,0.3)] transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/40 hover:shadow-[0_0_35px_-10px_rgba(147,51,234,0.4)] md:flex-row"
          >
            {/* Left: Image */}
            <div className="flex w-full justify-center md:w-1/3">
              <img
                src={b.hero}
                alt={b.name}
                className="h-32 w-48 rounded-xl border border-slate-700 object-cover"
              />
            </div>

            {/* Right: Details */}
            <div className="w-full md:w-2/3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">{b.name}</h2>
                <span className="rounded-full bg-gradient-to-r from-purple-500 to-blue-600 px-2 py-[2px] text-[10px] font-bold uppercase">
                  Verified
                </span>
              </div>

              <p className="mt-1 text-xs text-slate-400">
                Country: {b.country}
              </p>
              <p className="mt-1 text-sm text-slate-300">{b.description}</p>

              <div className="mt-2 grid grid-cols-2 text-xs text-slate-300">
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium text-yellow-400">
                    {b.rating}
                  </span>
                </div>
                <div className="text-right">{b.regulation}</div>

                <div>
                  <span className="text-slate-400">Deposit: </span>
                  <span className="font-medium text-white">{b.minDeposit}</span>
                </div>
                <div className="text-right">
                  <span className="text-slate-400">Platforms: </span>
                  <span className="font-medium text-white">{b.platforms}</span>
                </div>
              </div>

              {b.verified && (
                <div className="mt-2 flex items-center gap-1 text-[11px] text-green-400">
                  <CheckCircle2 className="h-3 w-3" /> Verified Broker
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <a
                  href={`https://${b.name.toLowerCase()}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-blue-600 px-3 py-1.5 text-[11px] transition hover:bg-blue-700"
                >
                  Visit Website
                </a>
                <p className="text-[10px] text-slate-400">{b.regulation}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
